import { useMemo } from "react";
import { getLocale } from "umi";
import { unitConversion } from "@/pages/datalab/utils/utils";
import dataFormat from "@home/utils/dbDataFormat/dataFormat";
import { LANGUAGE_EN } from "@home/_config/config";
import { ChartOption } from "../_config/_interface";
import {formatDisplayValue} from "@customComp/Chart/_config/_utils";

type TPiece = {
  gte?: number;
  gt?: number;
  lte: number;
  label: string;
}
export type TMapType = 'world'; // 后期可以加入其他类型
export interface MapChartProps {
  mapType?: TMapType;
  chartData: {
    metric?: string;
    value_type?: string;
    map_chart_value?: Array<{
      code: string;
      value: string;
      percent?: string;
    }>;
    min: string | number;
    max: string | number;
  };
  metricInfo: {
    accuracy: number;
  };
  chartConfig: {
    mapColors?: {
      colors?: Array<{ hexa: string; }>;
    };
  };
}

export interface NameMap {
  [code: string]: {
    name?: string;
    name_en?: string;
    name_zh?: string;
  };
}

/**
 * 获取地图的图例分段配置
 * @param min
 * @param max
 * @param count 具体分成几段
 * @param accuracy label显示的小数位数
 * @param isPercent 是否为百分比
 */
export const getPieces = ({min, max, count, accuracy, isPercent}: {
  min: number;
  max: number;
  count: number;
  accuracy: number;
  isPercent: boolean;
}): TPiece[] => {
  if (count <= 0) {
    throw new Error("Count must be greater than 0");
  }
  
  if (min === max) {
    return [{
      gte: min,
      lte: max,
      label: isPercent ? `${min}%` : unitConversion(min, true)
    }];
  }
  
  const [start, end] = min < max ? [min, max] : [max, min];
  const range = end - start;
  const step = range / count;
  const intervals: TPiece[] = [];
  
  for (let i = 0;i < count;i++) {
    const lower = dataFormat({
      value: start + i * step,
      formatRule: {type: "decimals", decimal: accuracy},
      fillZero: true,
      emptyValue: ''
    });
    const upper = dataFormat({
      value: start + (i + 1) * step,
      formatRule: {type: "decimals", decimal: accuracy},
      fillZero: true
    });
    
    const lt = isPercent ? `${lower}%` : unitConversion(lower, true);
    const rt = isPercent ? `${upper}%` : unitConversion(upper, true);
    
    intervals.push({
      ...(i === 0 ? { gte: Number(lower) } : { gt: Number(lower) }),
      lte: Number(upper),
      label: `${lt} - ${rt}`,
    });
  }
  
  return intervals;
};

/**
 * 设置 地图和图例的颜色 (只是修改配置, 没有复杂计算, 所以没有使用useMemo)
 * @param option
 * @param colors
 */
export const setMapColor = ({option, colors}: {
  option: ChartOption;
  colors: string[];
}): ChartOption => {
  const visualMap = option.visualMap as any;
  const { min, max } = visualMap?.[0] || {};
  const inRange = {
    color: min === max ? [colors[4]] : colors
  };
  return {
    ...option,
    visualMap: [{
      ...visualMap?.[0],
      inRange
    }]
  };
};

/**
 * 计算地图放大缩小后zoom值
 * @param zoom
 * @param type
 */
export const calculateMapZoom = (zoom: number, type: 'zoomIn' | 'zoomOut'): number => {
  let mapZoomNow = zoom;
  if (type === 'zoomIn' && mapZoomNow < 15) {
    mapZoomNow += 1;
  } else if (type === 'zoomOut' && mapZoomNow > 2) {
    mapZoomNow -= 1;
  }
  return mapZoomNow;
};
/**
 * 修改option中的zoom值, (只是修改配置, 没有复杂计算, 所以没有使用useMemo)
 * @param option
 * @param zoom
 */
export const setMapZoom = ({option, zoom}: {
  option: ChartOption;
  zoom: number;
}): ChartOption => {
  const serie = option?.series?.[0];
  return {
    ...option,
    series: [{
      ...serie,
      zoom
    }]
  };
};

/**
 * 修改option中的series相关配置, (只是修改配置, 没有复杂计算, 所以没有使用useMemo)
 * @param option
 * @param series
 */
export const setMapSeriesOption = (option, series): ChartOption => {
  const oldSerie = option?.series?.[0];
  return {
    ...option,
    series: [{
      ...oldSerie,
      ...series,
    }]
  };
};

/**
 * 获取当前地图图表的中心 经纬度 (在放大缩小地图时, 可以同时设置center, 保证放大缩小不会导致中心恢复原点)
 * @param mapChart
 */

export const getMapCenter = (mapChart) => {
  
  const mapSeries = mapChart?.getModel()?.getSeriesByType('map')?.[0];
  
  if (mapSeries) {
    const coordSys = mapSeries.coordinateSystem;
    const viewRect = coordSys.getViewRect();
    
    // 计算视图中心(像素坐标)
    const pixelCenter = [
      viewRect.x + viewRect.width / 2,
      viewRect.y + viewRect.height / 2
    ];
    
    // 转换为经纬度返回
    return coordSys.pointToData(pixelCenter);
  }
};

/**
 * 获取地图的基本配置
 * @param props
 * @param namesMap 存储了中英文映射对象
 */
export const useMapOptions = (props: MapChartProps, namesMap: NameMap) => {
  const { mapType = 'world', chartData, metricInfo } = props;
  const isEn = getLocale() === LANGUAGE_EN;
  const option: ChartOption = useMemo(() => {
    const { metric, value_type: valueType, map_chart_value: mapChartValue } = chartData || {};
    const metricName = metric?.replace(/(t[0-2]_)/, '');
    const isPercent = valueType === 'percent';
    const data = mapChartValue?.map((item) => ({
      ...item,
      name: item?.code,
      valueIsPercent: isPercent,
      value: isPercent ? item?.value?.replace('%', '') : item?.value,
      metricValue: formatDisplayValue({
        value: item?.value,
        isPercent,
        accuracy: metricInfo?.accuracy || 0,
        unit: metricInfo?.unit || 'bmk',
      }),
    }));
    let { min, max } = chartData || {};
    if (isPercent && typeof min === 'string') {
      min = min?.replace('%', '');
      max = max?.replace('%', '');
    }
    // 后端返回的 min 和 max 是string类型或者 null, null的时候不显示地图图例
    const isShowVisualMap = min && max && (mapChartValue?.length > 0);
    
    return {
      visualMap: [
        {
          show: !!isShowVisualMap,
          realtime: false,
          calculable: false,
          type: 'piecewise',
          inverse: true,
          text: ['', metricName],
          itemWidth: 10,
          itemHeight: 24,
          min: Number(min) || 0,
          max: Number(max) || 0,
          precision: 2,
          pieces: isShowVisualMap ? getPieces({
            min: Number(min),
            max: Number(max),
            count: 5,
            accuracy: metricInfo.accuracy,
            isPercent
          }) : [],
          showLabel: true,
          itemGap: 0,
          itemSymbol: 'rect',
          textStyle: {
            lineHeight: 20,
            color: '#838697',
            fontSize: 12,
            textBorderWidth: 2,
            textBorderColor: '#fff',
          },
          hoverLink: true,
          outOfRange: {
            color: '#ffdf33',
          },
          inRange: {
            color: [],
          }
        },
      ],
      series: [
        {
          type: 'map',
          map: `dbl_${mapType}`,
          roam: 'move',
          nameProperty: 'code',
          zoom: 1.2,
          label: {
            show: false,
            formatter: ({name: code}: {name: string;}) => {
              const { name_en, name_zh } = namesMap?.[code] || {};
              return (isEn ? name_en : name_zh) || code || '-';
            }
          },
          select: {
            label: {
              color: '#000',
            },
          },
          itemStyle: {
            normal: {
              borderColor: 'rgba(190, 190, 190, 0.5)',
              borderWidth: 1,
              areaColor: '#f2f2f2',
            },
            emphasis: {
              label: {
                show: true,
                color: '#000',
              },
              itemStyle: {
                borderColor: '#000',
                borderWidth: '2',
                borderType: 'solid',
                shadowColor: 'rgba(0, 0, 0, 0.5)',
                shadowBlur: 10,
              },
            },
          },
          data: data || [],
        },
      ],
      tooltip: {
        formatter: (params: any) => {
          const { name: code, percent, metricValue } = params?.data || { name: params?.name };
          const { name_en = '-', name_zh = '-', name } = namesMap?.[code] || {};
          const countryName = (isEn ? name_en : name_zh) || name || code || '-';
          const shareOfAll = percent ? `${percent}%` : '-';
          return `<div class='dbl-chart-tooltip' style='grid-template-columns: repeat(3, max-content)'>
            <div class='dbl-chart-tooltip-column-left'>${isEn ? 'Country' : '国家'}</div>
            <div class='dbl-chart-tooltip-column-right'>${metricName}</div>
            <div class='dbl-chart-tooltip-column-right'>${isEn ? 'Share of All' : '全局占比'}</div>
            <div class='dbl-chart-tooltip-column-left'>${countryName}</div>
            <div class='dbl-chart-tooltip-column-right dbl-chart-tooltip-bold-font'>${metricValue || '-'}</div>
            <div class='dbl-chart-tooltip-column-right dbl-chart-tooltip-bold-font'>${shareOfAll}</div>
          </div>`;
        },
        trigger: 'item',
        enterable: false,
        confine: true,
        appendTo: 'body',
        padding: 0,
        borderColor: 'none',
        borderRadius: 'none',
        borderWidth: 0,
        backgroundColor: 'none',
      },
    };
  }, [chartData, isEn, mapType, metricInfo.accuracy, namesMap]);
  
  return option;
};